import React from "react";
import type { ColumnsType } from "rc-table/es/interface";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import SenderLimitsSidebar, {
  DEFAULT_SENDER_DAILY_QUOTA_MAX,
  type SenderLimits,
} from "../../components/Campaigns/components/SenderLimitsSidebar";
import LinkedinSendersCampaignsModal from "../../components/Modals/LinkedinSendersCampaignsModal";
import { Button, DataTable, LeadSenderAvatar, Radio, SearchInput, Select, ServerTablePagination } from "../../components/ui";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { ownerSelector } from "../../store/store";
import { fetchUserOwnersWithWorkspaces } from "../../utils";
import { buildPaginationPayload, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from "../../utils/pagination";
import axios from "../../utils/axios";
import {
  type CampaignFlowState,
  fetchCampaignDraft,
  draftSearchFromCampaignId,
  resolveFlowScope,
  resolveCampaignIdFromStateOrSearch,
  upsertCampaignDraft,
} from "./campaignDraftFlow";

type SenderRow = {
  key: string;
  ownerId: string;
  name: string;
  subtitle: string;
  profileImage: string;
  activity: string;
  hasLinkedinToken: boolean;
  hasActiveOrPausedCampaign: boolean;
  activeOrPausedCampaignCount: number;
  limits: {
    first: string;
    second: string;
    third: string;
  };
  dailyUsage: {
    invitations: number;
    messages: number;
    visits: number;
    follows: number;
  };
  dailyQuota: {
    invitations: number;
    messages: number;
    visits: number;
    follows: number;
  };
};

type OwnersApiItem = {
  ownerId: string;
  name?: string;
  email?: string;
  profileImage?: string;
  publicIdentifier?: string;
  hasLinkedinToken?: boolean;
  hasActiveOrPausedCampaign?: boolean;
  activeOrPausedCampaignCount?: number;
  dailyQuota?: {
    invitations?: number;
    messages?: number;
    visits?: number;
    follows?: number;
  };
  dailyUsage?: {
    invitations?: number;
    messages?: number;
    visits?: number;
    follows?: number;
  };
};

type OwnersApiResponse = {
  success?: boolean;
  owners?: OwnersApiItem[];
  pagination?: {
    page?: number;
    limit?: number;
    total?: number;
  };
};

export default function CampaignCreateSendersStep() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { run: runContinue, isPending: isContinuing } = useAsyncAction();
  const flowState = (location.state as CampaignFlowState | null) || {};
  const campaignId = resolveCampaignIdFromStateOrSearch(flowState, searchParams.get("campaignId"));
  const { owner, currentUser, currentWorkspace } = useSelector(ownerSelector);
  const [senderSearch, setSenderSearch] = React.useState("");
  const [senderStatus, setSenderStatus] = React.useState("");
  const [subscriptionFilter, setSubscriptionFilter] = React.useState("");
  const [page, setPage] = React.useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = React.useState(false);
  const [selectedSenderKey, setSelectedSenderKey] = React.useState<string>("");
  const [senderRows, setSenderRows] = React.useState<SenderRow[]>([]);
  const [isLimitsSidebarOpen, setIsLimitsSidebarOpen] = React.useState(false);
  const [savingLimits, setSavingLimits] = React.useState(false);
  const [isLinkedinWarningOpen, setIsLinkedinWarningOpen] = React.useState(false);
  const [pendingStepTwoPayload, setPendingStepTwoPayload] = React.useState<CampaignFlowState["stepTwoPayload"]>();
  const [activeSenderId, setActiveSenderId] = React.useState("");
  const [activeSenderName, setActiveSenderName] = React.useState("Bojana Vojnovic");
  const [activeSenderAvatar, setActiveSenderAvatar] = React.useState("");
  const [activeSenderLimits, setActiveSenderLimits] = React.useState<SenderLimits>({
    maxFollowsPerDay: 40,
    maxMessagesPerDay: 40,
    maxConnectionRequestsPerDay: 40,
    maxProfileViewsPerDay: 40,
  });

  const SENDER_STATUS_OPTIONS = React.useMemo(
    () => [
      { value: "", label: "Select status" },
      { value: "available", label: "Available" },
      { value: "paused", label: "Paused" },
    ],
    []
  );

  const SUBSCRIPTION_OPTIONS = React.useMemo(
    () => [
      { value: "", label: "All Linkedin subscriptions" },
      { value: "premium", label: "Premium" },
      { value: "sales-nav", label: "Sales Navigator" },
    ],
    []
  );

  const resolveScope = React.useCallback(() => {
    return resolveFlowScope({
      flowState,
      owner,
      currentUser,
      currentWorkspace,
    });
  }, [flowState, owner, currentUser, currentWorkspace]);

  const persistStepTwoAndNavigate = React.useCallback(
    (stepTwoPayload: NonNullable<CampaignFlowState["stepTwoPayload"]>) =>
      runContinue(async () => {
        const { userId, workspaceId } = resolveScope();
        const loader = toast.loading("Saving changes...");
        try {
          const draftResponse = await upsertCampaignDraft({
            campaignId,
            ownerId: stepTwoPayload.senderOwnerId,
            userId,
            workspaceId,
            executorOwnerId: stepTwoPayload.senderOwnerId,
            senderOwnerIds: stepTwoPayload.senderOwnerIds,
          });
          const nextCampaignId = draftResponse?.campaignId || campaignId;
          toast.update(loader, {
            render: "Saved successfully.",
            type: "success",
            isLoading: false,
            autoClose: 3000,
            closeOnClick: true,
          });
          navigate(
            {
              pathname: "../sequence",
              search: draftSearchFromCampaignId(nextCampaignId),
            },
            {
              state: {
                ...flowState,
                campaignId: nextCampaignId,
                stepTwoPayload,
              },
            }
          );
        } catch (error) {
          toast.update(loader, {
            render: "Unable to save changes.",
            type: "error",
            isLoading: false,
            autoClose: 3000,
            closeOnClick: true,
          });
        }
      }),
    [campaignId, flowState, navigate, resolveScope, runContinue]
  );

  const fetchSenders = React.useCallback(async () => {
    const { userId, workspaceId, ownerId } = resolveScope();
    if (!userId && !ownerId) {
      setSenderRows([]);
      return;
    }

    setLoading(true);
    try {
      const { page: apiPage, limit: apiLimit } = buildPaginationPayload(page, rowsPerPage);
      const response = (await fetchUserOwnersWithWorkspaces(
        apiPage,
        apiLimit,
        senderSearch.trim(),
        userId,
        workspaceId,
        ownerId
      )) as OwnersApiResponse;

      if (!response?.success) {
        throw new Error("Unable to fetch senders");
      }

      const mappedRows: SenderRow[] = (response.owners || []).map((item) => {
        const invitationQuota = Number(item.dailyQuota?.invitations || 0);
        const invitationUsage = Number(item.dailyUsage?.invitations || 0);
        const messageQuota = Number(item.dailyQuota?.messages || 0);
        const messageUsage = Number(item.dailyUsage?.messages || 0);
        const followQuota = Number(item.dailyQuota?.follows || 0);
        const followUsage = Number(item.dailyUsage?.follows || 0);
        const visitQuota = Number(item.dailyQuota?.visits || 0);
        const visitUsage = Number(item.dailyUsage?.visits || 0);

        return {
          key: String(item.ownerId),
          ownerId: String(item.ownerId),
          name: item.name || item.email || "LinkedIn Sender",
          subtitle: `Up to ${invitationQuota} connection/day`,
          profileImage: item.profileImage || "",
          activity: item.hasLinkedinToken ? "Available" : "Unavailable",
          hasLinkedinToken: Boolean(item.hasLinkedinToken),
          hasActiveOrPausedCampaign: Boolean(item.hasActiveOrPausedCampaign),
          activeOrPausedCampaignCount: Number(item.activeOrPausedCampaignCount || 0),
          limits: {
            first: `${invitationUsage}/${invitationQuota}`,
            second: `${messageUsage}/${messageQuota}`,
            third: `${followUsage}/${followQuota}`,
          },
          dailyUsage: {
            invitations: invitationUsage,
            messages: messageUsage,
            follows: followUsage,
            visits: visitUsage,
          },
          dailyQuota: {
            invitations: invitationQuota,
            messages: messageQuota,
            follows: followQuota,
            visits: visitQuota,
          },
        };
      });

      setSenderRows(mappedRows);
    } catch (error) {
      console.log(error);
      toast.error("Unable to fetch LinkedIn senders");
      setSenderRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, resolveScope]);

  React.useEffect(() => {
    fetchSenders();
  }, [fetchSenders]);

  const [draftSenderOwnerId, setDraftSenderOwnerId] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    const hydrateDraftSender = async () => {
      if (!campaignId) return;
      const { userId, workspaceId, ownerId } = resolveScope();
      try {
        const draftResponse = await fetchCampaignDraft({
          campaignId,
          ownerId,
          userId,
          workspaceId,
        });
        if (cancelled) return;
        const campaign = draftResponse?.campaign;
        const senderOwnerId = campaign?.ownerId ? String(campaign.ownerId) : "";
        if (senderOwnerId) setDraftSenderOwnerId(senderOwnerId);
      } catch (error) {
        console.log(error);
      }
    };
    void hydrateDraftSender();
    return () => {
      cancelled = true;
    };
  }, [campaignId, resolveScope]);

  const handleSaveSenderLimits = async () => {
    if (!activeSenderId) {
      toast.error("Unable to resolve sender");
      return;
    }

    setSavingLimits(true);
    try {
      await axios.post("/owner/updateDailyQuota", {
        ownerId: activeSenderId,
        dailyQuota: {
          follows: Number(activeSenderLimits.maxFollowsPerDay || 0),
          invitations: Number(activeSenderLimits.maxConnectionRequestsPerDay || 0),
          messages: Number(activeSenderLimits.maxMessagesPerDay || 0),
          visits: Number(activeSenderLimits.maxProfileViewsPerDay || 0),
        },
      });

      toast.success("Sender limits updated successfully");
      setIsLimitsSidebarOpen(false);
      await fetchSenders();
    } catch (error) {
      console.log(error);
      toast.error("Unable to update sender limits");
    } finally {
      setSavingLimits(false);
    }
  };

  const visibleRows = React.useMemo(
    () =>
      senderRows.filter((row) => {
        // Product requirement: only show available senders in this step.
        if (!row.hasLinkedinToken) return false;
        const matchesSearch =
          !senderSearch ||
          row.name.toLowerCase().includes(senderSearch.toLowerCase()) ||
          row.subtitle.toLowerCase().includes(senderSearch.toLowerCase());
        const matchesStatus =
          !senderStatus ||
          (senderStatus === "available" ? row.hasLinkedinToken : false);
        const matchesSubscription = !subscriptionFilter || subscriptionFilter === "";
        return matchesSearch && matchesStatus && matchesSubscription;
      }),
    [senderRows, senderSearch, senderStatus, subscriptionFilter]
  );

  const connectedSenderCount = React.useMemo(
    () => senderRows.filter((row) => row.hasLinkedinToken).length,
    [senderRows]
  );

  // Drop stale selection (e.g. draft ownerId for a disconnected account hidden from the table).
  React.useEffect(() => {
    if (loading) return;
    if (!selectedSenderKey) return;
    const stillVisible = visibleRows.some((row) => row.key === selectedSenderKey);
    if (!stillVisible) setSelectedSenderKey("");
  }, [loading, selectedSenderKey, visibleRows]);

  // Prefer connected draft / route-state sender when present in the visible list.
  React.useEffect(() => {
    if (loading) return;
    if (selectedSenderKey) return;
    const preferred =
      String(flowState.stepTwoPayload?.senderOwnerId || "").trim() || draftSenderOwnerId;
    if (!preferred) return;
    const preferredVisible = visibleRows.find((row) => row.key === preferred);
    if (preferredVisible) setSelectedSenderKey(preferredVisible.key);
  }, [
    loading,
    selectedSenderKey,
    draftSenderOwnerId,
    flowState.stepTwoPayload?.senderOwnerId,
    visibleRows,
  ]);

  // Single-sender workspaces: only one LinkedIn-connected row — pre-select so Continue works without an extra click.
  React.useEffect(() => {
    if (loading) return;
    if (selectedSenderKey) return;
    if (visibleRows.length !== 1) return;
    setSelectedSenderKey(visibleRows[0].key);
  }, [loading, selectedSenderKey, visibleRows]);

  const handleContinue = () => {
    if (!visibleRows.length) {
      toast.error("Connect a LinkedIn account first, then select a sender to continue.");
      return;
    }
    if (!selectedSenderKey) {
      toast.error("Please select a sender to continue.");
      return;
    }
    // Only allow senders shown in the table (connected / has LinkedIn token).
    const selectedSender = visibleRows.find((row) => row.key === selectedSenderKey);
    if (!selectedSender?.ownerId) {
      toast.error("Please select an available LinkedIn sender to continue.");
      return;
    }
    if (!selectedSender.hasLinkedinToken) {
      toast.error("Selected sender is disconnected. Reconnect LinkedIn and try again.");
      return;
    }

    const stepTwoPayload = {
      senderOwnerId: selectedSender.ownerId,
      senderOwnerIds: [selectedSender.ownerId],
    };

    if (selectedSender.hasActiveOrPausedCampaign) {
      setPendingStepTwoPayload(stepTwoPayload);
      setIsLinkedinWarningOpen(true);
      return;
    }
    void persistStepTwoAndNavigate(stepTwoPayload);
  };

  const canContinue =
    !loading &&
    visibleRows.length > 0 &&
    Boolean(selectedSenderKey) &&
    visibleRows.some((row) => row.key === selectedSenderKey);

  const senderColumns = React.useMemo<ColumnsType<SenderRow>>(
    () => [
      {
        title: "",
        dataIndex: "key",
        key: "select",
        width: 52,
        render: (_value, row) => (
          <Radio
            checked={selectedSenderKey === row.key}
            onChange={(checked) => {
              setSelectedSenderKey(checked ? row.key : "");
            }}
            label={`Select ${row.name}`}
            labelClassName="sr-only"
            className="justify-center"
            name="sender-selection"
          />
        ),
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        width: 280,
        render: (_value, row) => (
          <div className="flex items-center gap-2">
            <LeadSenderAvatar picture={row.profileImage} className="h-7 w-7" />
            <div className="flex flex-col">
              <span className="text-sm font-medium text-dark-black">{row.name}</span>
              <span className="text-xs text-dark-grey">{row.subtitle}</span>
            </div>
          </div>
        ),
      },
      {
        title: "Activity",
        dataIndex: "activity",
        key: "activity",
        width: 170,
        render: (value: string, row) => (
          <span
            className={[
              "inline-flex rounded-[4px] px-2 py-1 text-xs font-medium",
              row.hasLinkedinToken ? "bg-success-bg text-success" : "bg-light-grey text-dark-grey",
            ].join(" ")}
          >
            {value}
          </span>
        ),
      },
      {
        title: "Daily sending limits",
        dataIndex: "limits",
        key: "limits",
        width: 360,
        render: (limits: SenderRow["limits"]) => (
          <div className="flex items-center gap-3 text-xs text-dark-black">
            <span className="inline-flex items-center gap-1">
              <img src="/icons/person.svg" alt="" aria-hidden="true" className="h-3 w-3" />
              {limits.first}
            </span>
            <span className="inline-flex items-center gap-1">
              <img src="/icons/weight.svg" alt="" aria-hidden="true" className="h-3 w-3" />
              {limits.second}
            </span>
            <span className="inline-flex items-center gap-1">
              <img src="/icons/tick.svg" alt="" aria-hidden="true" className="h-3 w-3" />
              {limits.third}
            </span>
          </div>
        ),
      },
      {
        title: "Configure",
        dataIndex: "key",
        key: "configure",
        width: 110,
        render: (_value, row) => (
          <button
            type="button"
            className="inline-flex items-center justify-center rounded p-1 hover:bg-primary"
            aria-label="Configure sender"
            onClick={() => {
              setActiveSenderName(row.name);
              setActiveSenderId(row.ownerId);
              setActiveSenderAvatar(row.profileImage || "");
              setActiveSenderLimits({
                maxFollowsPerDay: row.dailyQuota.follows,
                maxMessagesPerDay: row.dailyQuota.messages,
                maxConnectionRequestsPerDay: row.dailyQuota.invitations,
                maxProfileViewsPerDay: row.dailyQuota.visits,
              });
              setIsLimitsSidebarOpen(true);
            }}
          >
            <img src="/icons/setting.svg" alt="" aria-hidden="true" className="h-4 w-4" />
          </button>
        ),
      },
    ],
    [selectedSenderKey]
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 flex flex-wrap items-center justify-between gap-2 border-b border-light-grey bg-white px-4 py-3 sm:px-6">
        <Button
          variant="outline"
          size="sm"
          className="border-black bg-white text-dark-black"
          iconSrc="/icons/arrow_left.svg"
          showBefore
          onClick={() =>
            navigate(
              {
                pathname: "..",
                search: draftSearchFromCampaignId(campaignId),
              },
              { state: { ...flowState, campaignId } }
            )
          }
        >
          Back
        </Button>
        <Button
          variant="primary"
          size="sm"
          iconSrc="/icons/arrow_right.svg"
          showAfter
          isLoading={isContinuing}
          isDisabled={!canContinue}
          onClick={handleContinue}
        >
          {isContinuing ? "Saving..." : "Continue"}
        </Button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {!loading && connectedSenderCount === 0 ? (
        <div className="shrink-0 px-4 py-4 sm:px-6">
          <p className="text-sm text-dark-grey">
            No connected LinkedIn senders in this workspace. Connect an account under{" "}
            <button
              type="button"
              className="font-medium text-brand-blue underline-offset-2 hover:underline"
              onClick={() => navigate("/teams")}
            >
              Accounts
            </button>{" "}
            before continuing.
          </p>
        </div>
      ) : null}

      <div className="shrink-0 px-4 pb-4 sm:px-6">
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="w-full md:max-w-[260px]">
            <SearchInput
              value={senderSearch}
              onChange={setSenderSearch}
              onDebouncedChange={setSenderSearch}
              placeholder="Search senders"
              size="sm"
            />
          </div>
          <div className="w-full md:max-w-[190px]">
            <Select options={SENDER_STATUS_OPTIONS} value={senderStatus} onChange={setSenderStatus} size="sm" />
          </div>
          <div className="w-full md:max-w-[260px]">
            <Select
              options={SUBSCRIPTION_OPTIONS}
              value={subscriptionFilter}
              onChange={setSubscriptionFilter}
              size="sm"
            />
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 px-4 pb-6 sm:px-6">
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-[8px] border border-light-grey bg-white">
          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<SenderRow>
              columns={senderColumns}
              data={visibleRows}
              rowKey="key"
              tableLayout="fixed"
              rowClassName={(row) => (selectedSenderKey === row.key ? "bg-[#f5f5fa]" : "")}
            />
          </div>
          <ServerTablePagination
            page={page}
            pageSize={rowsPerPage}
            total={Math.max(visibleRows.length, connectedSenderCount)}
            rowsPerPageOptions={[10, 15, 25, 50]}
            onPageChange={setPage}
            onPageSizeChange={(nextSize) => {
              setRowsPerPage(nextSize);
              setPage(0);
            }}
            loading={loading}
          />
        </div>
      </div>
      </div>

      <SenderLimitsSidebar
        isOpen={isLimitsSidebarOpen}
        onClose={() => setIsLimitsSidebarOpen(false)}
        senderName={activeSenderName}
        senderAvatarSrc={activeSenderAvatar}
        linkedInAvatarVariant="sender"
        value={activeSenderLimits}
        max={DEFAULT_SENDER_DAILY_QUOTA_MAX}
        onChange={setActiveSenderLimits}
        onSave={savingLimits ? undefined : handleSaveSenderLimits}
      />

      <LinkedinSendersCampaignsModal
        isOpen={isLinkedinWarningOpen}
        onClose={async () => {
          setIsLinkedinWarningOpen(false);
          const payload = pendingStepTwoPayload;
          setPendingStepTwoPayload(undefined);
          if (!payload?.senderOwnerId) return;
          await persistStepTwoAndNavigate(payload);
        }}
      />
    </div>
  );
}
